import { omit } from 'lodash-es'

import { CATEGORY_INFO_MAP, SELECT_MODE } from '@/constants'

import { findValuesByKeys } from '@/utils'

import config from '../config'

const MULTIPLE_COMPONENT_LIST = ['OrgMultipleSelectFliter', 'OrgMultipleChoiceFilter']
// const SINGLE_COMPONENT_LIST = ['OrgSelectFilter']

/**
 * 判断当前组件类型是否可用
 * @param item 组件配置
 * @returns
 */
export const isRightType = (item: any) => {
  return Object.keys(config).includes(item.specific_type || item.type)
}

// 处理 TreeSelect 组件的分类
const dealTreeSelectCategory = (item: any) => {
  const { category, label } = findValuesByKeys(item, ['category', 'label'])

  // 如果是成本中心，则返回成本中心
  if (label?.includes(CATEGORY_INFO_MAP.CostCenter.label)) {
    return CATEGORY_INFO_MAP.CostCenter.category
  }
  return category?.toLocaleLowerCase()?.includes('org')
    ? CATEGORY_INFO_MAP.Organize.category
    : category
}

// 处理 BigDataSelect 组件的分类
const dealBigDataSelect = (type: string, item: any) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { searchType, category } = item || {}

  return {
    category
  }
}

// 无法通过配置转换的组件处理函数
export const SpecialHandleMap: Record<string, (type: string, item: any, parent?: any) => any> = {
  /** 前置处理函数 */
  beforeDeal: (type: string, item: any, parent?: any) => {
    const { elementProps, formItemProps } = item || {}
    const { dictionary, name, label, mode } = findValuesByKeys(item, [
      'dictionary',
      'name',
      'label',
      'mode'
    ])

    // 宿舍地址可搜索
    if (['HRM_DORMITORY_ADDRESS'].includes(dictionary)) {
      return {
        ...item,
        elementProps: {
          ...item.elementProps,
          mode: 'single',
          popup: 'single'
        },
        type: 'SearchSelect'
      }
    }

    // ModelPage/合同/联系人/教育经历/健康证 样式
    if (['ModelPage', 'Contract', 'Concat', 'Education', 'HealthCertificate'].includes(type)) {
      // 块级标题是否展示，若没展示则展示内部的标题
      const blockTitleFlag = parent?.title && parent?.is_show_title !== false
      const isModelPage = type === 'ModelPage'
      return {
        ...item,
        formItemProps: {
          ...item.formItemProps,
          className: 'adm-form-item-no-padding'
        },
        elementProps: {
          ...item.elementProps,
          is_show_title: !blockTitleFlag,
          title: blockTitleFlag && !isModelPage ? '' : parent?.title
        }
      }
    }

    // 附件, 若 formItemProps 有 label 则不展示 label
    if (type === 'FormImage') {
      return {
        ...item,
        elementProps: {
          ...(elementProps || {}),
          ...(formItemProps?.label || item.label ? { label: '' } : { label: label })
        }
      }
    }

    // 人员选择组件，处理 mode 为 null 的情况
    if (type === 'Personnel') {
      const configMode = ['single', null]?.includes(mode) ? 'single' : 'multiple'
      return {
        ...item,
        mode: configMode,
        elementProps: {
          ...item.elementProps,
          mode: configMode
        }
      }
    }

    // 异动-员工,PC 分页下拉选人呢
    if (type === 'SearchSelect' && dictionary === 'EmployeeDict') {
      return {
        ...item,
        type: 'Personnel'
      }
    }

    // 异动属性组件
    if (['DynamicsAdjustmentList', 'DynamicsElementList']?.includes(type) && label === '异动项') {
      return {
        name: 'details',
        disabled: '@disabled',
        elementProps: {
          userId: '@userId',
          is_show: '@is_show'
        },
        formItemProps: {
          label: ''
        }
      }
    }

    // 人员选择用统一的组件类型, Personnel 组件默认多选，但 SearchFetchSelect 组件默认单选
    if (type === 'SearchFetchSelect') {
      // mode 可能为 null
      const tempMode = mode || 'single'
      return {
        ...item,
        type: 'Personnel',
        elementProps: {
          ...item.elementProps,
          mode: tempMode,
          config: {
            ...item.elementProps?.config,
            mode: tempMode
          }
        }
      }
    }

    // 银行名称
    if (type === 'DictionaryBanksSelect') {
      const rest = omit(item, ['method', 'url', 'initArgument', 'dataPath', 'fieldItem'])
      return {
        ...rest,
        category: 'BankName',
        type: 'BigDataSelect'
      }
    }

    // 日期类型
    if (type === 'DatePicker') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { picker, ...restItem } = item || {}
      const { picker: precision, type: datePickerType } = findValuesByKeys(item, ['picker', 'type'])
      const tempType = precision || datePickerType
      // 兼容 data(旧的拼写错误)
      const dateType = ['date', 'data'].includes(tempType) ? 'day' : tempType
      return {
        ...restItem,
        elementProps: {
          ...item.elementProps,
          type: dateType,
          precision: dateType
        }
      }
    }
    return item
  },
  TreeSelect: (type: string, item: any) => {
    const mode =
      item.mode ||
      (MULTIPLE_COMPONENT_LIST.includes(type) ? SELECT_MODE.MULTIPLE : SELECT_MODE.SINGLE)

    // 兼容老的配置
    return {
      ...item,
      category: dealTreeSelectCategory(item),
      mode
    }
  },
  BigDataSelect: (type: string, item: any) => {
    const { url, method, dataPath, initArgument, elementProps, ...rest } = item || {}
    return {
      ...rest,
      ...dealBigDataSelect(type, item),
      elementProps: {
        ...elementProps,
        config: {
          ...elementProps?.config,
          ...(url ? { url, method, dataPath, initArgument } : {})
        }
      }
    }
  }
}

// 最终通用处理字段信息
export const dealFieldInfo = (item: any, parent?: any) => {
  // 特殊处理字段信息函数
  const excludeList = ['type', 'specific_type']
  // 组件原始类型
  const originalType = item.specific_type || item.type
  // 最终转换类型
  const formType = config[originalType]

  const specialHandle = SpecialHandleMap[formType] || ((type: string, item: any) => item)

  // 排除
  const finalItem = specialHandle(
    originalType,
    SpecialHandleMap.beforeDeal(originalType, omit(item, excludeList), parent)
  )

  return {
    ...finalItem,
    type: finalItem?.type || formType
  }
}
